import React, { useState, useEffect } from "react";
import { X, Check, ChevronDown, ChevronLeft, ChevronRight, Image as ImageIcon, Trash2 } from "lucide-react";
import bookingApi from "../../api/booking";
import { MediaUploadService } from "../../services/MediaUploadService";
import { useToast } from "../../context/ToastContext";

const formatDisputeStatus = (status = "") => {
  const s = String(status || "").toLowerCase().trim();
  if (s === "created") return "Under Review";
  if (s === "restored") return "Resolved";
  if (s === "cancelled") return "Cancelled";
  return status ? status.replace(/_/g, " ") : "Under Review";
};

export default function DisputeModal({
  isOpen,
  onClose,
  initialView = "raise", // "raise" | "success" | "view"
  mode = "standard", // "standard" | "cancellation"
  bookingId: propBookingId,
  bookingData = {},
  eventData = {},
}) {
  const { showSuccess, showError, showWarning } = useToast();
  const [view, setView] = useState(initialView); // "raise" | "success" | "view"

  // Form State
  const [category, setCategory] = useState("");
  const [concern, setConcern] = useState("");
  const [evidenceFiles, setEvidenceFiles] = useState([]);
  const [openFaq, setOpenFaq] = useState(0); // Index of open FAQ accordion
  const [submitting, setSubmitting] = useState(false);
  const [contextData, setContextData] = useState(null);
  const [disputeRecord, setDisputeRecord] = useState(null);
  const [selectedImageIndex, setSelectedImageIndex] = useState(null);

  const activeBookingId = propBookingId || bookingData?.bookingId || bookingData?._id;
  const isCancellationMode = mode === "cancellation";

  // Load context and active dispute details from backend
  useEffect(() => {
    if (!isOpen) return;
    setView(initialView);
    setCategory("");
    setConcern("");
    setEvidenceFiles([]);

    if (activeBookingId) {
      bookingApi.getDisputeContext(activeBookingId)
        .then((res) => {
          if (res.data?.success) {
            setContextData(res.data.data);
          }
        })
        .catch((err) => console.error("Error loading dispute context:", err));

      bookingApi.getDispute(activeBookingId)
        .then((res) => {
          if (res.data?.success && res.data.data) {
            setDisputeRecord(res.data.data);
            if (res.data.data.category) setCategory(res.data.data.category);
            if (res.data.data.concern) setConcern(res.data.data.concern);
          }
        })
        .catch((err) => console.error("Error checking dispute:", err));
    }
  }, [isOpen, initialView, activeBookingId]);

  if (!isOpen) return null;

  // Resolved Display Values
  const displayBookingId = contextData?.referenceId || bookingData.referenceId || activeBookingId || "4004556";
  const displayUserName = contextData?.userName || bookingData.userName || bookingData.customerName || bookingData.professionalName || "User";
  const displayLocation = contextData?.location || bookingData.location || eventData.location?.city || eventData.location?.address || eventData.city || "Delhi";
  
  const formatDate = (d) => {
    if (!d) return "19 Mar · 10 AM";
    const dateObj = new Date(d);
    if (isNaN(dateObj.getTime())) return String(d);
    const dayMonth = dateObj.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
    const time = dateObj.toLocaleTimeString("en-IN", { hour: "numeric", minute: "2-digit", hour12: true });
    return `${dayMonth} · ${time}`;
  };

  const displaySession = contextData?.sessionTime || formatDate(bookingData.createdAt || eventData.startDate || eventData.date || eventData.eventDate);

  // File upload handler
  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const newFiles = files.map((file) => ({
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
    }));
    setEvidenceFiles((prev) => [...prev, ...newFiles]);
  };

  const removeFile = (index) => {
    setEvidenceFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!category) {
      showWarning("Please select a category.");
      return;
    }
    if (!concern.trim()) {
      showWarning("Please describe your concern.");
      return;
    }
    if (!activeBookingId) {
      showError("Booking reference not found.");
      return;
    }

    try {
      setSubmitting(true);

      // Upload media if files are selected in standard mode
      let uploadedMediaIds = [];
      if (!isCancellationMode && evidenceFiles.length > 0) {
        for (const item of evidenceFiles) {
          if (item.file) {
            try {
              const uploaded = await MediaUploadService.uploadMedia(item.file, {
                folder: "disputes",
                tags: ["dispute"],
              });
              if (uploaded?._id) {
                uploadedMediaIds.push(uploaded._id);
              }
            } catch (uploadErr) {
              console.error("Media upload error:", uploadErr);
            }
          }
        }
      }

      const payload = {
        category,
        concern,
        media: uploadedMediaIds,
      };

      const res = await bookingApi.raiseDispute(activeBookingId, payload);
      if (res.data?.success) {
        setDisputeRecord(res.data.data);
        setView("success");
        showSuccess(isCancellationMode ? "Cancellation request submitted successfully!" : "Dispute raised successfully!");
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        showError(res.data?.message || "Failed to submit dispute");
      }
    } catch (err) {
      showError(err.response?.data?.message || err.message || "Failed to submit request");
    } finally {
      setSubmitting(false);
    }
  };

  const faqs = [
    {
      q: "How long does dispute resolution take?",
      a: "Most disputes are resolved within 3–5 business days. Complex cases involving payouts may take up to 10 business days. You'll receive email updates at each stage.",
    },
    {
      q: "Can I cancel a dispute after submitting it?",
      a: "Yes, you can cancel or close an open dispute anytime before admin reaches a final decision.",
    },
    {
      q: "Can I dispute a payout that's more than 30 days old?",
      a: "Disputes must typically be raised within 30 days of the event completion date.",
    },
    {
      q: "What happens after I submit?",
      a: "Your dispute is sent to our dispute mediation team and the booking status is updated to Under Review.",
    },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 backdrop-blur-[2px] z-[900]"
        onClick={onClose}
      />

      {/* Modal Container */}
      <div className="fixed inset-0 z-[901] flex items-center justify-center p-3 sm:p-4 overflow-y-auto">
        <div
          className="bg-white rounded-[24px] shadow-2xl w-full max-w-[480px] sm:max-w-[500px] overflow-hidden my-auto relative animate-fade-in"
          style={{ maxHeight: "94vh", display: "flex", flexDirection: "column" }}
        >
          {/* Close button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 text-gray-500 hover:text-gray-900 transition-colors z-20"
            title="Close"
          >
            <X className="w-5 h-5 sm:w-6 sm:h-6" />
          </button>

          {/* ════════════════ VIEW 1: RAISE / CANCEL DISPUTE FORM ════════════════ */}
          {view === "raise" && (
            <div className="p-5 sm:p-6 overflow-y-auto hide-scrollbar space-y-4 sm:space-y-5">
              {/* Header */}
              <div>
                <h3 className="text-xl sm:text-[22px] font-extrabold text-gray-950 tracking-tight leading-tight">
                  {isCancellationMode ? "Cancel Booking" : "Raise a Dispute"}
                </h3>
                <p className="text-xs sm:text-[13px] text-gray-500 mt-1 leading-relaxed">
                  {isCancellationMode
                    ? "Please select a category and provide details for your cancellation request. Our team will review your request within 24–48 hours."
                    : "Tell us what went wrong with your booking or payout—our team will review your request within 24–48 hours. Disputes put your booking under review, hold payouts, and alert our team."}
                </p>
              </div>

              {/* Booking Info Box */}
              <div className="border border-orange-200/80 bg-[#FFFBF9] rounded-2xl p-3 sm:p-4 grid grid-cols-4 gap-2 text-left">
                <div>
                  <span className="text-[11px] sm:text-xs font-bold text-gray-900 block">Booking ID</span>
                  <span className="text-[11px] sm:text-xs text-gray-600 truncate block mt-0.5">{displayBookingId}</span>
                </div>
                <div>
                  <span className="text-[11px] sm:text-xs font-bold text-gray-900 block">User</span>
                  <span className="text-[11px] sm:text-xs text-gray-600 truncate block mt-0.5">{displayUserName}</span>
                </div>
                <div>
                  <span className="text-[11px] sm:text-xs font-bold text-gray-900 block">Location</span>
                  <span className="text-[11px] sm:text-xs text-gray-600 truncate block mt-0.5">{displayLocation}</span>
                </div>
                <div>
                  <span className="text-[11px] sm:text-xs font-bold text-gray-900 block">Session</span>
                  <span className="text-[11px] sm:text-xs text-gray-600 truncate block mt-0.5">{displaySession}</span>
                </div>
              </div>

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-4">
                {/* Category Selection */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5">
                    {isCancellationMode ? "Cancellation Category" : "Dispute Category"}
                  </label>
                  <div className="relative">
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full h-11 px-3.5 pr-9 rounded-xl border border-gray-200 bg-white text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FF4D12]/20 focus:border-[#FF4D12] appearance-none"
                    >
                      <option value="">Select a category</option>
                      <option value="Service issue">Service issue</option>
                      <option value="Payment issue">Payment issue</option>
                      <option value="Behaviour issue">Behaviour issue</option>
                      <option value="Other">Other</option>
                    </select>
                    <ChevronDown className="w-4 h-4 text-gray-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
                  </div>
                </div>

                {/* Describe concern */}
                <div>
                  <label className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5">
                    {isCancellationMode ? "Describe your reason" : "Describe your concern"}
                  </label>
                  <textarea
                    rows={4}
                    value={concern}
                    onChange={(e) => setConcern(e.target.value)}
                    placeholder={
                      isCancellationMode
                        ? "Please provide details regarding why you wish to cancel this booking..."
                        : "Be as specific as possible – include dates, amounts, and what you expected to happen..."
                    }
                    className="w-full p-3.5 rounded-xl border border-gray-200 text-xs sm:text-sm text-gray-800 focus:outline-none focus:ring-2 focus:ring-[#FF4D12]/20 focus:border-[#FF4D12] resize-none placeholder:text-gray-400"
                  />
                </div>

                {/* Upload Evidence & FAQs only in standard mode */}
                {!isCancellationMode && (
                  <>
                    {/* Upload Evidence */}
                    <div>
                      <label className="block text-xs sm:text-sm font-bold text-gray-900 mb-1.5">
                        Upload Evidence
                      </label>
                      
                      <div className="flex flex-wrap gap-2.5 items-center">
                        <label className="w-24 h-24 sm:w-28 sm:h-28 border border-gray-200 rounded-xl flex flex-col items-center justify-center p-2 text-center cursor-pointer hover:bg-gray-50 transition border-dashed">
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={handleFileUpload}
                            className="hidden"
                          />
                          <ImageIcon className="w-7 h-7 text-gray-500 mb-1" strokeWidth={1.5} />
                          <span className="text-[10px] sm:text-[11px] font-semibold text-gray-800 leading-tight">
                            Click to upload images
                          </span>
                          <span className="text-[8px] sm:text-[9px] text-gray-400 mt-0.5">
                            PNG, JPG, GIF up to 5MB
                          </span>
                        </label>

                        {evidenceFiles.map((item, idx) => (
                          <div key={idx} className="relative w-24 h-24 sm:w-28 sm:h-28 rounded-xl border border-gray-200 overflow-hidden group">
                            <img src={item.preview} alt="evidence" className="w-full h-full object-cover" />
                            <button
                              type="button"
                              onClick={() => removeFile(idx)}
                              className="absolute top-1.5 right-1.5 bg-black/60 hover:bg-red-600 text-white rounded-full p-1 transition"
                              title="Remove image"
                            >
                              <Trash2 className="w-3 h-3" />
                            </button>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Quick FAQ's Accordion */}
                    <div className="pt-2">
                      <h4 className="text-sm font-bold text-gray-900 mb-2">Quick FAQ's</h4>
                      <div className="space-y-2">
                        {faqs.map((faq, index) => {
                          const isOpenFaq = openFaq === index;
                          return (
                            <div
                              key={index}
                              className="border border-gray-200 rounded-xl overflow-hidden bg-white transition-colors"
                            >
                              <button
                                type="button"
                                onClick={() => setOpenFaq(isOpenFaq ? -1 : index)}
                                className="w-full p-3 text-left flex items-center justify-between text-xs sm:text-sm font-semibold text-gray-800 hover:bg-gray-50 transition"
                              >
                                <span>{faq.q}</span>
                                <ChevronDown
                                  className={`w-4 h-4 text-gray-500 shrink-0 transition-transform duration-200 ${
                                    isOpenFaq ? "rotate-180" : ""
                                  }`}
                                />
                              </button>
                              {isOpenFaq && (
                                <div className="px-3 pb-3 text-xs text-gray-500 leading-relaxed border-t border-gray-100 pt-2">
                                  {faq.a}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  </>
                )}

                {/* Submit Button (Small, Right Bottom Side) */}
                <div className="pt-3 flex justify-end">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="px-6 py-2.5 bg-[#FF4D12] hover:bg-[#e64415] text-white font-bold rounded-xl text-xs sm:text-sm transition shadow-sm active:scale-[0.98] disabled:opacity-60"
                  >
                    {submitting
                      ? "Submitting..."
                      : isCancellationMode
                      ? "Submit Request"
                      : "Submit Dispute"}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* ════════════════ VIEW 2: SUCCESS SCREEN ════════════════ */}
          {view === "success" && (
            <div className="py-12 sm:py-16 px-6 text-center flex flex-col items-center justify-center my-auto">
              {/* Big orange checkmark circle */}
              <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-[#FF4D12] text-white flex items-center justify-center mb-6 shadow-md">
                <Check className="w-10 h-10 sm:w-12 sm:h-12 stroke-[3.5]" />
              </div>

              {/* Title */}
              <h3 className="text-2xl sm:text-3xl font-extrabold text-gray-950 mb-2 tracking-tight">
                {isCancellationMode ? "Cancellation Requested" : "Dispute Raised"}
              </h3>

              {/* Subtitle */}
              <p className="text-gray-500 text-xs sm:text-sm max-w-xs mx-auto mb-8 leading-relaxed">
                Your case is under review.<br />
                You can track updates from the booking page.
              </p>

              {/* View Dispute Button */}
              <button
                type="button"
                onClick={() => setView("view")}
                className="w-full max-w-[260px] py-3 bg-[#FF4D12] hover:bg-[#e64415] text-white font-bold rounded-xl text-sm sm:text-base transition shadow-sm active:scale-[0.99]"
              >
                View Dispute Details
              </button>
            </div>
          )}

          {/* ════════════════ VIEW 3: VIEW DISPUTE DETAILS ════════════════ */}
          {view === "view" && (() => {
            const rawStatus = String(disputeRecord?.status || "created").toLowerCase();
            const isRestored = rawStatus === "restored";
            const isCancelled = rawStatus === "cancelled";
            const isResolvedOrCancelled = isRestored || isCancelled;

            const dispRefId = disputeRecord?.referenceId || (disputeRecord?._id ? `DID-${disputeRecord._id.slice(-6).toUpperCase()}` : displayBookingId);
            const mediaList = disputeRecord?.media || [];

            return (
              <div className="p-5 sm:p-6 overflow-y-auto hide-scrollbar space-y-4 sm:space-y-5">
                {/* Header: Dispute Reference ID & Booking ID */}
                <div className="pr-6">
                  <h3 className="text-xl sm:text-2xl font-extrabold text-gray-950 tracking-tight">
                    Dispute #{dispRefId}
                  </h3>
                  <p className="text-xs text-gray-500 font-medium mt-0.5">
                    Booking ID: <span className="font-semibold text-gray-700">#{displayBookingId}</span>
                  </p>
                </div>

                {/* Status & Category row */}
                <div className="space-y-1.5 text-xs sm:text-sm">
                  <div className="flex gap-4">
                    <span className="font-bold text-gray-950 w-20">Status</span>
                    <span className={`font-semibold ${
                      isRestored ? "text-[#489C65]" : isCancelled ? "text-[#FF4C18]" : "text-[#4285F4]"
                    }`}>
                      {formatDisputeStatus(disputeRecord?.status)}
                    </span>
                  </div>
                  <div className="flex gap-4">
                    <span className="font-bold text-gray-950 w-20">Category</span>
                    <span className="text-gray-600">{disputeRecord?.category || category || "Service issue"}</span>
                  </div>
                </div>

                {/* Describe your concern preview */}
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-gray-950 mb-2">
                    Concern Details
                  </h4>
                  <div className="border border-gray-200 rounded-xl p-3.5 bg-white text-xs sm:text-sm text-gray-600 leading-relaxed">
                    {disputeRecord?.concern || concern ||
                      "Professional arrived late and setup was incomplete before event start."}
                  </div>
                </div>

                {/* ── Timeline (Vertical dots, no line, no time) ── */}
                <div className="pt-1 pb-1">
                  <h4 className="text-base sm:text-lg font-bold text-gray-950 mb-3.5 tracking-tight">
                    Timeline
                  </h4>
                  <div className="space-y-3.5">
                    {/* Step 1: Dispute Raised */}
                    <div className="flex items-start gap-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#FF4C18] mt-1 shrink-0" />
                      <div>
                        <p className="text-xs sm:text-sm font-bold text-gray-900 leading-tight">
                          Dispute Raised
                        </p>
                        <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5 font-medium">
                          Submitted
                        </p>
                      </div>
                    </div>

                    {/* Step 2: Under Review */}
                    <div className="flex items-start gap-3">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#FF4C18] mt-1 shrink-0" />
                      <div>
                        <p className="text-xs sm:text-sm font-bold text-gray-900 leading-tight">
                          Under Review
                        </p>
                        <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5 font-medium">
                          {isResolvedOrCancelled ? "Completed" : "In progress"}
                        </p>
                      </div>
                    </div>

                    {/* Step 3: Resolved or Cancelled */}
                    <div className="flex items-start gap-3">
                      <span className={`w-2.5 h-2.5 rounded-full mt-1 shrink-0 ${
                        isResolvedOrCancelled ? "bg-[#FF4C18]" : "bg-gray-400"
                      }`} />
                      <div>
                        <p className="text-xs sm:text-sm font-bold text-gray-900 leading-tight">
                          {isCancelled ? "Cancelled" : "Resolved"}
                        </p>
                        <p className="text-[11px] sm:text-xs text-gray-400 mt-0.5 font-medium">
                          {isResolvedOrCancelled ? "Completed" : "Pending"}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Attachments from Media */}
                <div>
                  <h4 className="text-xs sm:text-sm font-bold text-gray-950 mb-2">Attachments</h4>
                  <div className="flex items-center gap-2.5 mb-1.5 flex-wrap">
                    {mediaList.length > 0 ? (
                      mediaList.map((item, idx) => {
                        const imgUrl = item?.secure_url || item?.url || (typeof item === "string" ? item : "");
                        return (
                          <div 
                            key={idx} 
                            onClick={() => setSelectedImageIndex(idx)}
                            className="w-16 h-16 sm:w-20 sm:h-20 rounded-xl overflow-hidden border border-gray-200 cursor-pointer hover:opacity-90 hover:scale-[1.03] transition-all shadow-xs"
                            title="Click to preview image"
                          >
                            <img
                              src={imgUrl}
                              alt="media"
                              className="w-full h-full object-cover"
                            />
                          </div>
                        );
                      })
                    ) : (
                      <span className="text-xs text-gray-400">No attachments uploaded</span>
                    )}
                  </div>
                </div>

                {/* Close / Done Button */}
                <div className="pt-2 flex justify-end">
                  <button
                    type="button"
                    onClick={onClose}
                    className="px-6 py-2.5 bg-[#FF4D12] hover:bg-[#e64415] text-white font-bold rounded-xl text-xs sm:text-sm transition shadow-sm active:scale-[0.98]"
                  >
                    Done
                  </button>
                </div>
              </div>
            );
          })()}
        </div>
      </div>

      {/* ════════════════ IMAGE POPUP LIGHTBOX VIEWER ════════════════ */}
      {selectedImageIndex !== null && (() => {
        const mediaList = disputeRecord?.media || [];
        const currentMedia = mediaList[selectedImageIndex];
        const currentUrl = currentMedia?.secure_url || currentMedia?.url || (typeof currentMedia === "string" ? currentMedia : "");
        const hasPrev = mediaList.length > 1;
        const hasNext = mediaList.length > 1;

        const handlePrev = (e) => {
          e.stopPropagation();
          setSelectedImageIndex((prev) => (prev > 0 ? prev - 1 : mediaList.length - 1));
        };

        const handleNext = (e) => {
          e.stopPropagation();
          setSelectedImageIndex((prev) => (prev < mediaList.length - 1 ? prev + 1 : 0));
        };

        return (
          <div
            className="fixed inset-0 z-[1000] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 select-none animate-fadeIn"
            onClick={() => setSelectedImageIndex(null)}
          >
            {/* Close Button */}
            <button
              type="button"
              onClick={() => setSelectedImageIndex(null)}
              className="absolute top-4 right-4 sm:top-6 sm:right-6 w-10 h-10 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors z-20"
              title="Close image"
            >
              <X className="w-6 h-6" />
            </button>

            {/* Previous Button */}
            {hasPrev && (
              <button
                type="button"
                onClick={handlePrev}
                className="absolute left-3 sm:left-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors z-20"
                title="Previous image"
              >
                <ChevronLeft className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            )}

            {/* Main Image */}
            <div
              className="relative max-w-[92vw] max-h-[86vh] flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <img
                src={currentUrl}
                alt="Enlarged attachment"
                className="max-w-full max-h-[86vh] object-contain rounded-xl shadow-2xl"
              />

              {/* Counter Pill */}
              {mediaList.length > 1 && (
                <div className="absolute -bottom-10 left-1/2 -translate-x-1/2 px-3 py-1 bg-black/60 text-white text-xs font-semibold rounded-full">
                  {selectedImageIndex + 1} / {mediaList.length}
                </div>
              )}
            </div>

            {/* Next Button */}
            {hasNext && (
              <button
                type="button"
                onClick={handleNext}
                className="absolute right-3 sm:right-6 w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-white/15 hover:bg-white/30 text-white flex items-center justify-center transition-colors z-20"
                title="Next image"
              >
                <ChevronRight className="w-6 h-6 sm:w-8 sm:h-8" />
              </button>
            )}
          </div>
        );
      })()}
    </>
  );
}
